import React from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { Loader2, Send, Sparkles, X } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SID_KEY = "cvai_assistant_sid";

const QUICK_CHIPS = [
  "How much does it cost?",
  "How does it work?",
  "Which plan is right for me?",
];

/** Render the assistant's markdown-lite replies (**bold**, - bullets) as clean JSX. */
const renderReply = (text) => {
  const lines = text.split("\n");
  return lines.map((line, li) => {
    const parts = line.split(/(\*\*[^*]+\*\*)/g).map((part, pi) =>
      part.startsWith("**") && part.endsWith("**") ? (
        <strong key={pi} className="text-white font-semibold">
          {part.slice(2, -2)}
        </strong>
      ) : (
        part
      )
    );
    const isBullet = line.trimStart().startsWith("- ");
    return (
      <span key={li} className={isBullet ? "flex gap-2 pl-1" : "block"}>
        {isBullet && <span className="text-zinc-500 shrink-0">•</span>}
        <span>{isBullet ? parts.map((p, i) => (typeof p === "string" ? p.replace(/^- /, "") : p)).map((p, i) => <span key={i}>{p}</span>) : parts}</span>
      </span>
    );
  });
};

const getSessionId = () => {
  let sid = localStorage.getItem(SID_KEY);
  if (!sid) {
    sid = crypto.randomUUID();
    localStorage.setItem(SID_KEY, sid);
  }
  return sid;
};

export const Assistant = () => {
  const [open, setOpen] = React.useState(false);
  const [messages, setMessages] = React.useState([]); // {role: 'user'|'assistant', text}
  const [input, setInput] = React.useState("");
  const [busy, setBusy] = React.useState(false);
  const scrollRef = React.useRef(null);

  React.useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, busy]);

  const send = async (text) => {
    const msg = (text ?? input).trim();
    if (!msg || busy) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", text: msg }]);
    setBusy(true);
    try {
      const { data } = await axios.post(
        `${API}/assistant/chat`,
        { message: msg, session_id: getSessionId() },
        { withCredentials: true }
      );
      setMessages((m) => [...m, { role: "assistant", text: data.reply }]);
    } catch (err) {
      setMessages((m) => [
        ...m,
        {
          role: "assistant",
          text:
            err?.response?.status === 429
              ? "You've hit today's chat limit — email us at getcvaipro@gmail.com and a human will help."
              : "Something hiccuped on my side — try again in a moment.",
        },
      ]);
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      {/* Floating button */}
      <motion.button
        initial={{ scale: 0, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ delay: 1.2, type: "spring", stiffness: 200, damping: 16 }}
        onClick={() => setOpen((o) => !o)}
        className="fixed bottom-5 right-5 z-50 inline-flex h-13 w-13 items-center justify-center rounded-full bg-white text-black shadow-[0_8px_30px_rgba(255,255,255,0.2)] hover:scale-105 transition-transform p-3.5"
        aria-label={open ? "Close assistant" : "Open AI assistant"}
        data-testid="assistant-fab"
      >
        {open ? <X className="h-5 w-5" /> : <Sparkles className="h-5 w-5" />}
      </motion.button>

      {/* Panel */}
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: 24, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 24, scale: 0.96 }}
            transition={{ type: "spring", stiffness: 300, damping: 28 }}
            className="fixed bottom-20 right-5 z-50 w-[calc(100vw-2.5rem)] max-w-[400px] rounded-3xl border border-white/10 bg-zinc-950/95 backdrop-blur-xl shadow-2xl overflow-hidden flex flex-col"
            style={{ maxHeight: "min(560px, 70vh)" }}
            data-testid="assistant-panel"
          >
            {/* Header */}
            <div className="flex items-center gap-2.5 border-b border-white/10 px-5 py-4">
              <span className="inline-flex h-7 w-7 items-center justify-center rounded-md bg-gradient-to-br from-blue-500/80 to-purple-600/80 ring-1 ring-white/10">
                <Sparkles className="h-3.5 w-3.5 text-white" />
              </span>
              <div>
                <p className="text-sm font-semibold text-white leading-tight">CV AI PRO Assistant</p>
                <p className="text-[11px] text-zinc-500">Ask about plans, your order, or CV advice</p>
              </div>
            </div>

            {/* Messages */}
            <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-4 space-y-3 min-h-[220px]" data-testid="assistant-messages">
              {messages.length === 0 && (
                <div className="pt-2">
                  <p className="text-sm text-zinc-400 text-center text-balance">
                    Hi! I can answer anything about CV AI PRO — or give you real resume advice.
                  </p>
                  <div className="mt-4 flex flex-wrap gap-2 justify-center">
                    {QUICK_CHIPS.map((chip, i) => (
                      <button
                        key={chip}
                        onClick={() => send(chip)}
                        className="rounded-full border border-white/10 bg-white/5 px-3.5 py-1.5 text-xs text-zinc-300 hover:bg-white/10 hover:text-white transition-colors"
                        data-testid={`assistant-chip-${i}`}
                      >
                        {chip}
                      </button>
                    ))}
                  </div>
                </div>
              )}
              {messages.map((m, i) => (
                <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                  <div
                    className={`max-w-[85%] rounded-2xl px-4 py-2.5 text-sm leading-relaxed whitespace-pre-wrap ${
                      m.role === "user"
                        ? "bg-white text-black rounded-br-md"
                        : "bg-white/5 border border-white/10 text-zinc-200 rounded-bl-md"
                    }`}
                    data-testid={`assistant-message-${i}`}
                  >
                    {m.role === "assistant" ? renderReply(m.text) : m.text}
                  </div>
                </div>
              ))}
              {busy && (
                <div className="flex justify-start" data-testid="assistant-typing">
                  <div className="bg-white/5 border border-white/10 rounded-2xl rounded-bl-md px-4 py-3 flex gap-1.5">
                    {[0, 1, 2].map((d) => (
                      <span
                        key={d}
                        className="h-1.5 w-1.5 rounded-full bg-zinc-500 animate-bounce"
                        style={{ animationDelay: `${d * 0.15}s` }}
                      />
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Input */}
            <form
              onSubmit={(e) => {
                e.preventDefault();
                send();
              }}
              className="border-t border-white/10 p-3 flex items-center gap-2"
            >
              <input
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder="Ask me anything…"
                maxLength={1000}
                className="flex-1 bg-black/40 border border-white/10 rounded-full px-4 py-2.5 text-sm text-white placeholder:text-zinc-600 focus:outline-none focus:ring-1 focus:ring-white/30"
                data-testid="assistant-input"
              />
              <button
                type="submit"
                disabled={busy || !input.trim()}
                className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-white text-black hover:bg-zinc-200 transition-colors disabled:opacity-50"
                aria-label="Send message"
                data-testid="assistant-send"
              >
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
              </button>
            </form>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
